import React, { createContext, useEffect, useRef, useState } from 'react';
import { usersAPI } from '../services/api';
import { trackEvent } from '../services/analytics';

export const FilterContext = createContext(null);

// Entspricht Config.DEFAULT_SEARCH_ADDRESS im Backend.
export const DEFAULT_SEARCH_ADDRESS =
  process.env.REACT_APP_DEFAULT_SEARCH_ADDRESS || 'Bahnhofplatz 1, 8400 Winterthur';
const DEFAULT_ADDRESS = DEFAULT_SEARCH_ADDRESS;
const ADDRESS_STORAGE_KEY = 'winterthur_aktiv_search_address';

// Startpunkt & Startzeit (start_address, departure_date, departure_time) bleiben erhalten
const RESET_VALUES = {
  age_min: null,
  age_max: null,
  categories: [],
  indoor_outdoor: null,
  cost_range: null,
  duration: null,
  wheelchair_accessible: false,
  event_type: null,
};

// Filtergruppen für das Umami-Event filter-apply (ohne Startpunkt/-zeit).
// Alter null entspricht 0–12 («keine Präferenz»), wie in AgeFilter.
const filterSnapshot = (f) => ({
  age: `${Number.isInteger(f.age_min) ? f.age_min : 0}-${Number.isInteger(f.age_max) ? f.age_max : 12}`,
  categories: [...(f.categories || [])].sort().join(','),
  indoor_outdoor: f.indoor_outdoor || '',
  cost_range: f.cost_range || '',
  duration: f.duration || '',
  wheelchair: !!f.wheelchair_accessible,
  freely_accessible: f.event_type === 'freely_accessible',
});

export const FilterProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [filters, setFilters] = useState(() => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const minutes = now.getMinutes();
    const roundedMinutes = minutes <= 30 ? 30 : 60;
    const roundedTime = new Date(now);
    roundedTime.setSeconds(0, 0);
    roundedTime.setMinutes(roundedMinutes);
    if (roundedMinutes === 60) {
      roundedTime.setHours(roundedTime.getHours() + 1);
      roundedTime.setMinutes(0);
    }
    const defaultTime = roundedTime.toTimeString().slice(0, 5);
    const savedAddress = localStorage.getItem(ADDRESS_STORAGE_KEY) || DEFAULT_ADDRESS;
    return {
      start_address: savedAddress,
      departure_date: today,
      departure_time: defaultTime,
      age_min: null,
      age_max: null,
      categories: [],
      indoor_outdoor: null,
      cost_range: null,
      duration: null,
      wheelchair_accessible: false,
      event_type: null,
    };
  });

  // Listen to user changes from AuthContext via window events
  useEffect(() => {
    const handleUserChange = (event) => {
      const userData = event.detail;
      setUser(userData);
      
      // Update start_address when user changes
      if (userData) {
        // Fallback: current_search_address → home_address → DEFAULT_ADDRESS
        const address = userData.current_search_address || userData.home_address || DEFAULT_ADDRESS;
        setFilters(prev => ({ ...prev, start_address: address }));
      } else {
        // User logged out, restore last saved address
        const savedAddress = localStorage.getItem(ADDRESS_STORAGE_KEY) || DEFAULT_ADDRESS;
        setFilters(prev => ({ ...prev, start_address: savedAddress }));
      }
    };

    window.addEventListener('auth-user-changed', handleUserChange);
    
    // Get initial user state
    const initialUser = window.__currentUser;
    if (initialUser) {
      setUser(initialUser);
      const address = initialUser.current_search_address || initialUser.home_address || DEFAULT_ADDRESS;
      setFilters(prev => ({ ...prev, start_address: address }));
    }

    return () => {
      window.removeEventListener('auth-user-changed', handleUserChange);
    };
  }, []);

  const updateFilter = async (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    
    // If start_address changed, persist locally and (if logged in) in the database
    if (key === 'start_address') {
      localStorage.setItem(ADDRESS_STORAGE_KEY, value);
      if (user) {
        try {
          await usersAPI.updateSettings({ current_search_address: value });
        } catch (error) {
          console.error('Failed to save search address:', error);
        }
      }
    }
  };

  // Umami: Filter greifen live – filter-apply erst 1 s nach der letzten Änderung,
  // verglichen mit dem zuletzt gemeldeten Stand (nicht bei jeder Slider-Bewegung).
  const trackedSnapshotRef = useRef(null);
  useEffect(() => {
    const snapshot = filterSnapshot(filters);
    if (!trackedSnapshotRef.current) {
      trackedSnapshotRef.current = snapshot;
      return undefined;
    }
    const timer = setTimeout(() => {
      const changed = Object.keys(snapshot).filter((k) => snapshot[k] !== trackedSnapshotRef.current[k]);
      trackedSnapshotRef.current = snapshot;
      if (changed.length === 0) return;
      const [ageMin, ageMax] = snapshot.age.split('-').map(Number);
      const data = {
        changed: changed.join(','),
        age_changed: changed.includes('age'),
        category_count: (filters.categories || []).length,
        indoor_outdoor: snapshot.indoor_outdoor,
        cost_range: snapshot.cost_range,
        duration: snapshot.duration,
      };
      if (!(ageMin === 0 && ageMax === 12)) {
        data.age_min = ageMin;
        data.age_max = ageMax;
      }
      trackEvent('filter-apply', data);
    }, 1000);
    return () => clearTimeout(timer);
  }, [filters]);

  const resetFilters = () => {
    trackEvent('filter-reset');
    // Reset gilt als neuer Ausgangsstand, damit danach kein filter-apply folgt
    trackedSnapshotRef.current = filterSnapshot(RESET_VALUES);
    setFilters(prev => ({ ...prev, ...RESET_VALUES }));
  };

  const value = {
    filters,
    updateFilter,
    resetFilters,
  };

  return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>;
};
